import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Image as ImageIcon, Play } from "lucide-react";
import { projectMeta } from "@/data/lampContent";
import HeroLottie from "@/components/HeroLottie";

const CHARACTER_SRC =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_solar-lamp-builder/artifacts/oan3k2zg_home-gif-2.gif";

export default function Home() {
  return (
    <main
      className="min-h-screen flex flex-col bg-[var(--bg-base)]"
      data-testid="home-page"
    >
      {/* HERO — composed illustration with scroll fly-out */}
      <section
        className="w-full border-b-2 border-[var(--border-heavy)]/10"
        data-testid="home-hero-section"
      >
        <HeroLottie />
      </section>

      {/* WELCOME + SELECTOR — sits above the fixed hero overlay via z-10.
          min-h-screen ensures it fully covers the viewport once scrolled to,
          so the cream hero underneath never bleeds through at the top. */}
      <section
        className="relative z-10 min-h-screen w-full flex flex-col items-center justify-center px-6 py-12 sm:py-16 bg-white"
        data-testid="home-welcome-section"
      >
        {/* character */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="w-56 sm:w-72"
          data-testid="welcome-character-wrap"
        >
          <img
            src={CHARACTER_SRC}
            alt="Friendly guide waving hello"
            className="w-full h-auto select-none"
            draggable={false}
            data-testid="welcome-character"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.28, duration: 0.4 }}
          className="mt-1 font-hand text-3xl text-[var(--text-main)]"
          data-testid="home-greeting"
        >
          {projectMeta.greeting}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="mt-3 max-w-2xl text-center font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight"
          data-testid="home-headline"
        >
          These are the{" "}
          <span className="relative inline-block">
            <span className="relative z-10">steps</span>
            <span
              aria-hidden
              className="absolute -bottom-1 left-0 right-0 h-2.5 bg-[var(--accent)]/70 -rotate-1 rounded-sm z-0"
            />
          </span>
          <br />
          you have to follow today!
        </motion.h1>

        {/* Selector — three chunky green buttons */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.4 }}
          className="mt-10 flex flex-col sm:flex-row gap-4 sm:gap-6"
          data-testid="home-selector"
        >
          <SelectorCard
            to="/video"
            label="Video Guide"
            icon={<Play strokeWidth={3} className="h-4 w-4" />}
            testId="home-cta-video"
          />
          <SelectorCard
            to="/discuss"
            label="Picture Guide"
            icon={<ImageIcon strokeWidth={3} className="h-4 w-4" />}
            testId="home-cta-picture"
          />
        </motion.div>
      </section>

      <footer
        className="relative z-10 w-full text-center py-6 text-xs font-heading text-[var(--text-muted)] bg-white"
        data-testid="home-footer"
      >
        Game Designer Kit
      </footer>
    </main>
  );
}

function SelectorCard({ to, label, icon, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="arcade-btn min-w-[180px] justify-between"
    >
      <span className="flex items-center gap-2">
        {icon}
        {label}
      </span>
      <ArrowUpRight strokeWidth={3} className="h-4 w-4" />
    </Link>
  );
}
